import { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LeadsTable } from "../components/leads/LeadsTable";
import {
    Users,
    Search,
    TrendingUp,
    Calendar,
    RefreshCw,
    Building2,
    AlertCircle,
    Settings
} from "lucide-react";
import { toast } from "sonner";
import { fetchAvailableLeads } from "../services/crmServices/leadService";
import { fetchLeadStatuses } from "../services/crmServices/leadStatusService";
import { fetchDesks } from "../services/crmServices/deskService";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { subDays, isAfter } from "date-fns";
import { getFullName } from "../services/common/util";
import { searchPositions } from "../services/crmServices/positionService";

export function ActiveCustomers() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [allLeads, setAllLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [activeClientStatuses, setActiveClientStatuses] = useState([]);
    const [managerDesks, setManagerDesks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");
    const [selectedDesk, setSelectedDesk] = useState("all");
    const [currentFilters, setCurrentFilters] = useState({}); // Add filter state for LeadsTable filters
    const [stats, setStats] = useState({
        total: 0,
        thisWeek: 0,
        thisMonth: 0,
        byStatus: {},
        byDesk: {}
    });

    useEffect(() => {
        if (user.position.role === "agent") {
            toast.error("Access denied. Manager privileges required.");
            return;
        }
        loadData();
    }, [user.position.role]);

    useEffect(() => {
        applyFilters();
    }, [allLeads, searchTerm, selectedStatus, selectedDesk, currentFilters, activeClientStatuses]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load desks for manager
            const desksResponse = await fetchDesks();
            let userDesks = [];

            if (desksResponse?.data) {
                if (user.position.role === "admin") {
                    userDesks = desksResponse.data;
                } else if (user.position.role === "floorManager") {
                    const deskManagerPositionsResponse = await searchPositions({
                        role: "deskManager",
                        floor: user.position.floor
                    });
                    const deskManagerPositions = deskManagerPositionsResponse?.data || [];
                    // For managers, show only their managed desks
                    userDesks = desksResponse.data.filter(
                        (desk) =>
                            desk.manager &&
                            deskManagerPositions.some((position) => position._id === desk.manager.position)
                    );
                } else {
                    // For agents, show only their assigned desks
                    userDesks = desksResponse.data.filter((desk) => desk.manager && desk.manager._id === user._id);
                }
                setManagerDesks(userDesks);
            }

            // Load lead statuses to get active client statuses
            const statusResponse = await fetchLeadStatuses();
            if (statusResponse?.data) {
                setLeadStatuses(statusResponse.data);
                const activeStatuses = statusResponse.data.filter((status) => status.isActiveClient);
                setActiveClientStatuses(activeStatuses);

                if (activeStatuses.length === 0) {
                    setAllLeads([]);
                    calculateStats([], activeStatuses, userDesks);
                    return;
                }

                // Get manager's desk IDs
                const managerDeskIds = userDesks.map((desk) => desk._id);

                // Load leads with server-side filtering for active client statuses and desks
                // Use a high limit to get all active customers (or implement pagination)
                const activeStatusValues = activeStatuses.map((status) => status.value);
                const filters = {
                    statuses: activeStatusValues,
                    desk: managerDeskIds.length > 0 ? managerDeskIds : undefined
                };

                // Remove undefined filters
                Object.keys(filters).forEach(key => {
                    if (filters[key] === undefined) {
                        delete filters[key];
                    }
                });

                // Fetch with a high limit to get all active customers (max 1000 per request)
                // For more than 1000, we'd need to implement pagination
                const leadsResponse = await fetchAvailableLeads(1, 1000, filters);
                
                if (leadsResponse?.data) {
                    setAllLeads(leadsResponse.data);
                    calculateStats(leadsResponse.data, activeStatuses, userDesks);
                } else {
                    setAllLeads([]);
                    calculateStats([], activeStatuses, userDesks);
                }
            }
        } catch (error) {
            console.error("Error loading active customers:", error);
            toast.error("Failed to load active customers");
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (leads, statuses, desks) => {
        const now = new Date();
        const oneWeekAgo = subDays(now, 7);
        const oneMonthAgo = subDays(now, 30);

        const thisWeek = leads.filter((lead) => isAfter(new Date(lead.createdAt), oneWeekAgo)).length;
        const thisMonth = leads.filter((lead) => isAfter(new Date(lead.createdAt), oneMonthAgo)).length;

        const byStatus = {};
        statuses.forEach((status) => {
            byStatus[status.value] = leads.filter((lead) => lead.status === status.value).length;
        });

        const byDesk = {};
        desks.forEach((desk) => {
            byDesk[desk._id] = leads.filter((lead) => lead.desk?._id === desk._id).length;
        });

        setStats({
            total: leads.length,
            thisWeek,
            thisMonth,
            byStatus,
            byDesk
        });
    };

    const applyFilters = () => {
        let filtered = [...allLeads];

        // Apply search
        if (searchTerm.trim()) {
            filtered = filtered.filter(
                (lead) =>
                    lead.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.secondPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.desk?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    getFullName(lead.agent)?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter (from button or from currentFilters)
        const statusFilter = currentFilters.statuses || (selectedStatus !== "all" ? [selectedStatus] : undefined);
        if (statusFilter && statusFilter.length > 0) {
            filtered = filtered.filter((lead) => statusFilter.includes(lead.status));
        }

        // Apply desk filter (from button or from currentFilters)
        const deskFilter = currentFilters.desk || (selectedDesk !== "all" ? [selectedDesk] : undefined);
        if (deskFilter && deskFilter.length > 0) {
            filtered = filtered.filter((lead) => {
                const leadDeskId = lead.desk?._id || lead.desk;
                return deskFilter.includes(leadDeskId);
            });
        }

        // Apply agent filter from currentFilters
        if (currentFilters.agent && currentFilters.agent.length > 0) {
            filtered = filtered.filter((lead) => {
                const leadAgentId = lead.agent?._id || lead.agent;
                return currentFilters.agent.includes(leadAgentId);
            });
        }

        // Apply other filters from currentFilters (source, affiliate, etc.)
        if (currentFilters.source && currentFilters.source.length > 0) {
            filtered = filtered.filter((lead) => 
                lead.source && currentFilters.source.includes(lead.source)
            );
        }

        if (currentFilters.affiliate && currentFilters.affiliate.length > 0) {
            filtered = filtered.filter((lead) => 
                lead.affiliate && currentFilters.affiliate.includes(lead.affiliate)
            );
        }

        if (currentFilters.country && currentFilters.country.length > 0) {
            filtered = filtered.filter((lead) => 
                lead.country && currentFilters.country.includes(lead.country)
            );
        }

        setFilteredLeads(filtered);
    };

    // Add handler for LeadsTable filter changes
    const handleFilterChange = (newFilters) => {
        setCurrentFilters(newFilters);
    };

    const handleEdit = (lead) => {
        navigate(`/leads/edit/${lead._id}`);
    };

    const handleView = (lead) => {
        navigate(`/leads/view/${lead._id}`);
    };

    const getStatusConfig = (statusValue) => {
        return leadStatuses.find((status) => status.value === statusValue);
    };

    // Check access permissions
    if (user.position.role === "agent") {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <Card className="max-w-md mx-auto mt-8">
                    <CardContent className="text-center py-8">
                        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                        <p className="text-gray-600">Manager privileges required to access this page.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Active Customers
                            {user.position.role !== "admin" && (
                                <span className="text-lg font-normal text-gray-600 ml-2"></span>
                            )}
                        </h1>
                        <p className="text-gray-600">
                            Active customers from {user.position.role === "admin" ? "all desks" : "your managed desks"}{" "}
                            with status: {activeClientStatuses.map((s) => s.label).join(", ")}
                        </p>
                    </div>
                    <Button onClick={loadData} variant="outline" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-blue-600" />
                            <div>
                                <p className="text-sm text-gray-600">Total Active Customers</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                            <div>
                                <p className="text-sm text-gray-600">This Week</p>
                                <p className="text-2xl font-bold text-green-600">{stats.thisWeek}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Calendar className="h-5 w-5 text-purple-600" />
                            <div>
                                <p className="text-sm text-gray-600">This Month</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.thisMonth}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
                            <Building2 className="h-5 w-5 text-orange-600" />
                            <div>
                                <p className="text-sm text-gray-600">Managed Desks</p>
                                <p className="text-2xl font-bold text-orange-600">{managerDesks.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            {/* Manager Desks Info */}
            {managerDesks.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-4">
                    <Button
                        size="sm"
                        variant={selectedDesk === "all" ? "default" : "outline"}
                        onClick={() => setSelectedDesk("all")}
                    >
                        <Building2 className="h-3 w-3 mr-1" />
                        All Desks
                    </Button>
                    {managerDesks.map((desk) => (
                        <Button
                            key={desk._id}
                            size="sm"
                            variant={selectedDesk === desk._id ? "default" : "outline"}
                            onClick={() => setSelectedDesk(desk._id)}
                            className={`border-blue-600 ${selectedDesk === desk._id ? "text-white" : "text-blue-600"}`}
                        >
                            {desk.name} ({stats.byDesk[desk._id] || 0})
                        </Button>
                    ))}
                </div>
            )}
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
                <Button
                    size="sm"
                    variant={selectedStatus === "all" ? "default" : "outline"}
                    onClick={() => setSelectedStatus("all")}
                >
                    All Statuses ({stats.total})
                </Button>
                {activeClientStatuses.map((status) => (
                    <Button
                        key={status._id}
                        size="sm"
                        variant={selectedStatus === status.value ? "default" : "outline"}
                        onClick={() => setSelectedStatus(status.value)}
                        style={{
                            backgroundColor: selectedStatus === status.value ? status.color : undefined,
                            borderColor: status.color,
                            color: selectedStatus === status.value ? "white" : status.color
                        }}
                    >
                        {status.label} ({stats.byStatus[status.value] || 0})
                    </Button>
                ))}
            </div>
            {/* Filters */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-4 mb-6">
                <div className="relative w-full lg:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Search active customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* No Active Client Statuses Warning */}
            {!loading && activeClientStatuses.length === 0 && (
                <Card className="mb-6">
                    <CardContent className="text-center py-8">
                        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            No Active Client Statuses Configured
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Contact your administrator to configure which lead statuses should be considered
                            &quot;Active Clients&quot;.
                        </p>
                        {user.position.role === "admin" && (
                            <Button
                                onClick={() => navigate("/settings/lead-status")}
                                className=""
                            >
                                <Settings className="h-4 w-4 mr-2" />
                                Configure Active Client Statuses
                            </Button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* No Managed Desks Warning */}
            {managerDesks.length === 0 && user.position.role !== "admin" && (
                <Card className="mb-6">
                    <CardContent className="text-center py-8">
                        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Managed Desks Found</h3>
                        <p className="text-gray-600">
                            You don&apos;t have any desks assigned as manager. Contact your administrator to assign
                            desks to your management.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Active Customers Table */}
            {activeClientStatuses.length > 0 && managerDesks.length > 0 && (
                <LeadsTable
                    leads={filteredLeads}
                    selectedLeads={[]}
                    onSelectLead={() => {}}
                    isLeadSelected={() => false}
                    showBulkSelection={false}
                    onEdit={handleEdit}
                    onDelete={() => {}}
                    onView={handleView}
                    onTransfer={() => {}}
                    loading={loading}
                    initLeads={loadData}
                    showStatusBadges={true}
                    getStatusConfig={getStatusConfig}
                    showDeskInfo={true}
                    showAgentInfo={true}
                    currentFilters={currentFilters}
                    onFilterChange={handleFilterChange}
                />
            )}

            {/* No Results */}
            {activeClientStatuses.length > 0 && managerDesks.length > 0 && filteredLeads.length === 0 && !loading && (
                <Card>
                    <CardContent className="text-center py-8">
                        <Search className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">No active customers found</p>
                        <p className="text-sm text-gray-400">
                            {searchTerm || selectedStatus !== "all" || selectedDesk !== "all"
                                ? "Try adjusting your search or filters"
                                : "No leads with active client statuses exist in your managed desks yet"}
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
